import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFetch, type ApiErrorDto } from "../useFetch";
import type {
	CreatePaymentPayload,
	PaginatedResponse,
	PaymentExchangeRateResponse,
	PaymentResponse,
	UpdatePaymentPayload,
} from "../../core/types/services";

const EXCHANGE_RATE_CACHE_KEY = "servix_usd_to_bs_rate";
const EXCHANGE_RATE_REFRESH_MS = 60 * 60 * 1000;
const EXTERNAL_EXCHANGE_RATE_URLS = [
	"https://api.exchangerate.host/latest?base=USD&symbols=VES",
	"https://open.er-api.com/v6/latest/USD",
	"https://api.exchangerate-api.com/v4/latest/USD",
];

function parseExchangeRate(value: unknown): number | null {
	if (typeof value === "number") {
		return Number.isFinite(value) && value > 0 ? value : null;
	}

	if (typeof value === "string") {
		const raw = value.trim();
		if (!raw) return null;

		let normalized = raw.replace(/\s+/g, "");
		if (normalized.includes(",") && normalized.includes(".")) {
			normalized = normalized.replace(/\./g, "").replace(",", ".");
		} else if (normalized.includes(",")) {
			normalized = normalized.replace(",", ".");
		}

		const parsed = Number(normalized);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
	}

	return null;
}

function readCachedExchangeRate(): number | null {
	try {
		const raw = window.localStorage.getItem(EXCHANGE_RATE_CACHE_KEY);
		if (!raw) return null;
		const parsed = Number(raw);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
	} catch {
		return null;
	}
}

function writeCachedExchangeRate(rate: number): void {
	try {
		window.localStorage.setItem(EXCHANGE_RATE_CACHE_KEY, String(rate));
	} catch {
		// Ignore cache write failures.
	}
}

function extractRateFromPayload(payload: unknown): number | null {
	if (!payload || typeof payload !== "object") return null;

	const record = payload as Record<string, unknown>;
	return (
		parseExchangeRate(record.usd_to_bs) ??
		parseExchangeRate((record.rates as Record<string, unknown> | undefined)?.VES) ??
		parseExchangeRate((record.result as Record<string, unknown> | undefined)?.VES) ??
		parseExchangeRate(
			(record.conversion_rates as Record<string, unknown> | undefined)?.VES,
		)
	);
}

export function usePayments(page = 1, pageSize = 10) {
	const query = useMemo(() => ({ page, pageSize }), [page, pageSize]);

	return useFetch<PaginatedResponse<PaymentResponse>>({
		endpoint: "/payments",
		method: "GET",
		query,
	});
}

export function usePayment(id: number) {
	return useFetch<PaymentResponse>({
		endpoint: `/payments/${id}`,
		method: "GET",
		auto: id > 0,
	});
}

export function usePaymentExchangeRate() {
	const [data, setData] = useState<PaymentExchangeRateResponse | null>(() => {
		const cached = readCachedExchangeRate();
		if (!cached) return null;
		return {
			usd_to_bs: cached,
			source: "cache",
			fetched_at: new Date().toISOString(),
		};
	});
	const [error, setError] = useState<ApiErrorDto | null>(null);
	const [loading, setLoading] = useState(false);
	const [status, setStatus] = useState<number | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);

	const abort = useCallback(() => {
		abortControllerRef.current?.abort();
		abortControllerRef.current = null;
		setLoading(false);
	}, []);

	const reset = useCallback(() => {
		setData(null);
		setError(null);
		setLoading(false);
		setStatus(null);
	}, []);

	const execute = useCallback(async (): Promise<PaymentExchangeRateResponse | null> => {
		abortControllerRef.current?.abort();
		const controller = new AbortController();
		abortControllerRef.current = controller;

		setLoading(true);
		setError(null);

		for (const url of EXTERNAL_EXCHANGE_RATE_URLS) {
			try {
				const response = await fetch(url, {
					method: "GET",
					cache: "no-store",
					signal: controller.signal,
				});

				if (!response.ok) continue;

				const payload = (await response.json()) as unknown;
				const rate = extractRateFromPayload(payload);
				if (!rate) continue;

				const resolved: PaymentExchangeRateResponse = {
					usd_to_bs: rate,
					source: "external",
					fetched_at: new Date().toISOString(),
				};

				writeCachedExchangeRate(rate);
				setData(resolved);
				setStatus(200);
				setLoading(false);
				abortControllerRef.current = null;
				return resolved;
			} catch (caughtError) {
				if ((caughtError as Error).name === "AbortError") {
					setLoading(false);
					abortControllerRef.current = null;
					return null;
				}
			}
		}

		const cached = readCachedExchangeRate();
		if (cached) {
			const resolved: PaymentExchangeRateResponse = {
				usd_to_bs: cached,
				source: "cache",
				fetched_at: new Date().toISOString(),
			};
			setData(resolved);
			setStatus(200);
			setLoading(false);
			abortControllerRef.current = null;
			return resolved;
		}

		setStatus(503);
		setError({
			message: "No se pudo obtener la tasa de cambio en tiempo real.",
		});
		setLoading(false);
		abortControllerRef.current = null;
		return null;
	}, []);

	useEffect(() => {
		void execute().catch(() => null);
		const intervalId = window.setInterval(() => {
			void execute().catch(() => null);
		}, EXCHANGE_RATE_REFRESH_MS);

		return () => {
			window.clearInterval(intervalId);
			abortControllerRef.current?.abort();
			abortControllerRef.current = null;
		};
	}, [execute]);

	return {
		data,
		meta: null,
		error,
		loading,
		status,
		execute,
		abort,
		reset,
	};
}

export function useCreatePayment() {
	const fetch = useFetch<PaymentResponse, CreatePaymentPayload>({
		endpoint: "/payments",
		method: "POST",
		auto: false,
	});

	const create = (data: CreatePaymentPayload, files?: { payment_support?: File }) => {
		return fetch.execute({ body: data, files });
	};

	return { ...fetch, create };
}

export function useUpdatePayment(id: number) {
	const fetch = useFetch<PaymentResponse, UpdatePaymentPayload>({
		endpoint: `/payments/${id}`,
		method: "PATCH",
		auto: false,
	});

	const update = (data: UpdatePaymentPayload) => {
		return fetch.execute({ body: data });
	};

	return { ...fetch, update };
}

export function useDeletePayment() {
	const fetch = useFetch<PaymentResponse>({
		endpoint: "/payments",
		method: "DELETE",
		auto: false,
	});

	const remove = (id: number) => {
		return fetch.execute({ endpoint: `/payments/${id}` });
	};

	return { ...fetch, remove };
}
